import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { interval, Observable } from 'rxjs';
import { debounce, map } from 'rxjs/operators';

import { UserState } from '@app/auth/user.state';
import { MessageService } from '@app/messages/message.service';
import { Org } from '@app/orgs/org.model';
import { filterNullish } from '@app/shared/rxjs-utils';
import { SharedService } from '@app/shared/shared.service';

enum ExpTense {
  Past = 'expired',
  Future = 'expires',
}

/**
 * This component is used to display "non toast" messages.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, DatePipe, AsyncPipe],
  selector: 'app-messages',
  styleUrls: ['./messages.component.scss'],
  templateUrl: './messages.component.html',
})
export class MessagesComponent {
  private messageService = inject(MessageService);
  private userState = inject(UserState);
  private sharedService = inject(SharedService);

  private today = new Date();
  public ExpTense = ExpTense;
  public expTense?: ExpTense;
  public trialExpiresOn?: Date;
  public showPlanExpirationMessage$: Observable<boolean>;
  public loading$: Observable<boolean>;

  constructor() {
    this.loading$ = this.messageService.isLoading$.pipe(
      debounce((show) => interval(show ? 100 : 0)),
    );

    this.showPlanExpirationMessage$ = toObservable(this.userState.org).pipe(
      filterNullish(),
      map((o: Org) => {
        /**
         * We show a warning during the 30 days prior to reaching the expiration date and later
         * an error if we've crossed the expiration date.
         */
        if (o.limitationSummary.expiration) {
          this.trialExpiresOn = new Date(o.limitationSummary.expiration);

          this.expTense =
            this.sharedService.differenceInDays(this.trialExpiresOn, this.today) < 0
              ? ExpTense.Past
              : ExpTense.Future;

          return !this.sharedService.isDifferenceInDaysGreaterThan(
            this.trialExpiresOn,
            this.today,
            30,
          );
        } else {
          // Required if we switch to an org that does not have `freeTrialExpires` set.
          return false;
        }
      }),
    );
  }
}
